import React, { Component, useEffect } from 'react';
import { graphql } from 'react-relay';
import { BrowserRouter, Route, Redirect, Switch } from 'react-router-dom';
import { RelayEnvironmentProvider } from 'finish-line';
import { ToastContainer } from 'react-toastify';

import StoresContainer from '../containers/StoresContainer';
import { StoreProvider, HeaderView } from '../components';
import FastCashContainer from '../containers/FastCashContainer';
import BillingSettingsContainer from '../containers/BillingSettingsContainer';
import TransactionsDashboardContainer from '../containers/TransactionsDashboardContainer';
import CashbacksDashboardContainer from '../containers/CashbacksDashboardContainer';
import MembershipSettingsContainer from '../containers/MembershipSettingsContainer';
import MembershipsContainer from '../containers/MembershipsContainer';
import BannerSettingsContainer from '../containers/BannerSettingsContainer';
import SupportRequestContainer from '../containers/SupportRequestContainer';
import SupportRequestsContainer from '../containers/SupportRequestsContainer';
import SendMessageContainer from '../containers/SendMessageContainer';
import TransactionContainer from '../containers/TransactionContainer';
import CashbackRatesContainer from '../containers/CashbackRatesContainer';
import { createRelayEnvironment } from '../utils/relayHelpers';

import { QrCodeList, QrCodeShow } from './QrCodes';
import LoginView from './Login';
import { HappeningsList, HappeningNew, HappeningEdit } from './Happenings';
import { InvoiceList } from './Invoices';
import { OffersList, OfferNew, OfferEdit } from './Offers';
import { TopOffersList } from './TopOffers';
import { FlashOffersList } from './FlashOffers';
import { BrandNew, BrandEdit, BrandShow } from './Brands';
import BonusNew from './Bonus/BonusNew';
import Multibonus from './Bonus/Multibonus';
import ScheduleBonusList from './Bonus/ScheduleBonusList';
import CashbackNew from './Cashbacks/CashbackNew';
import CashbackShow from './Cashbacks/CashbackShow';
import CashbackEdit from './Cashbacks/CashbackEdit';
import { CategoryList, CategoryEdit, CategoryNew } from './Categories';
import { MallShow, MallEdit } from './MallInfo';
import { AdminList, AdminNew } from './Admins';
import Users from './Users';
import QuickSight from './QuickSight';
import {
  MessagesList,
  MessageEdit,
  MessageNew,
  AssociatedMessageNew,
} from './Messages';

const drawerWidth = 240;

class NoMatch extends Component {
  render() {
    return (
      <div className="container">
        The page you are looking for could not be found :(
      </div>
    );
  }
}

class NoAuthorized extends Component {
  render() {
    return (
      <div className="permission-error">
        You must be an admin to view the NEX Crossing CMS.
      </div>
    );
  }
}

class CheckAuthentication extends Component {
  render() {
    const { children, viewer } = this.props;
    const { currentUser } = viewer;

    return children({
      isSignedIn: !!currentUser,
      isAdmin: !!currentUser && currentUser.admin,
    });
  }
}

class IsSignedIn extends Component {
  render() {
    return (
      <StoreProvider
        {...this.props}
        container={CheckAuthentication}
        modern
        query={graphql`
          query routerQuery {
            viewer {
              currentUser {
                admin
              }
            }
          }
        `}
      />
    );
  }
}
const AuthedRoute = (props) => {
  const { component: ComponentToRender, ...otherProps } = props;

  return (
    <Route {...otherProps}>
      {(routeProps) => (
        <IsSignedIn {...routeProps}>
          {({ isSignedIn, isAdmin }) => {
            if (isSignedIn && isAdmin) {
              return <ComponentToRender {...routeProps} />;
            } else if (isSignedIn) {
              return <NoAuthorized />;
            } else {
              return <Redirect to="/login" />;
            }
          }}
        </IsSignedIn>
      )}
    </Route>
  );
};
const UnauthedRoute = (props) => {
  const { component: ComponentToRender, ...otherProps } = props;
  return (
    <Route {...otherProps}>
      {(routeProps) => (
        <IsSignedIn {...routeProps}>
          {({ isSignedIn }) =>
            isSignedIn ? (
              <Redirect to="/" />
            ) : (
              <ComponentToRender {...routeProps} />
            )
          }
        </IsSignedIn>
      )}
    </Route>
  );
};

const AppRouter = () => {
  return (
    <BrowserRouter>
      <RelayEnvironmentProvider create={createRelayEnvironment}>
        <div className="container">
          <ToastContainer />
          <div className="auth-container">
            <HeaderView />
            <div className="inner-container">
              <Switch>
                <Route
                  exact
                  path="/"
                  render={() => <Redirect to="/brands" />}
                />
                <UnauthedRoute path="/login" component={LoginView} />
                <AuthedRoute path="/brands/new" component={BrandNew} />
                <AuthedRoute
                  path="/brands/:brandId/cashback-rates"
                  component={CashbackRatesContainer}
                />
                <AuthedRoute
                  path="/brands/:brandId/edit"
                  component={BrandEdit}
                />
                <AuthedRoute
                  path="/brands/:brandId/invoices"
                  component={InvoiceList}
                />
                <AuthedRoute
                  path="/brands/:brandId/offers/new"
                  component={OfferNew}
                />
                <AuthedRoute
                  path="/brands/:brandId/offers-tab/:offersTab"
                  component={BrandShow}
                />
                <AuthedRoute path="/brands/:brandId" component={BrandShow} />
                <AuthedRoute path="/brands" component={StoresContainer} />
                <AuthedRoute
                  path="/fastcashes/:fastCashId/cashbacks/new"
                  component={CashbackNew}
                />
                <AuthedRoute
                  path="/fastcash/:fastCashId/memberships"
                  component={MembershipsContainer}
                />
                <AuthedRoute
                  path="/cashbacks/:cashbackId/edit"
                  component={CashbackEdit}
                />
                <AuthedRoute
                  path="/cashbacks/:cashbackId"
                  component={CashbackShow}
                />
                <AuthedRoute path="/fastcash/users" component={Users} />
                <AuthedRoute path="/fastcash" component={FastCashContainer} />
                <AuthedRoute
                  path="/cashbacks"
                  component={CashbacksDashboardContainer}
                />
                <AuthedRoute
                  path="/banner-settings"
                  component={BannerSettingsContainer}
                />
                <AuthedRoute path="/happenings/new" component={HappeningNew} />
                <AuthedRoute
                  path="/happenings/:happeningId/edit"
                  component={HappeningEdit}
                />
                <AuthedRoute
                  path="/happenings/tab/:type"
                  component={HappeningsList}
                />
                <AuthedRoute path="/happenings" component={HappeningsList} />
                <AuthedRoute path="/offers/new" component={OfferNew} />
                <AuthedRoute
                  path="/offers/:offerId/edit"
                  component={OfferEdit}
                />
                <AuthedRoute path="/offers/tab/:type" component={OffersList} />
                <AuthedRoute path="/offers" component={OffersList} />
                <AuthedRoute path="/top-offers" component={TopOffersList} />
                <AuthedRoute path="/flash-offers" component={FlashOffersList} />
                <AuthedRoute
                  path="/billing-settings"
                  component={BillingSettingsContainer}
                />
                <AuthedRoute
                  path="/membership-settings"
                  component={MembershipSettingsContainer}
                />
                <AuthedRoute path="/categories/new" component={CategoryNew} />
                <AuthedRoute
                  path="/categories/:categoryId/edit"
                  component={CategoryEdit}
                />
                <AuthedRoute path="/categories" component={CategoryList} />
                <AuthedRoute path="/mall-info/edit" component={MallEdit} />
                <AuthedRoute path="/mall-info/" component={MallShow} />
                <AuthedRoute path="/admins/new" component={AdminNew} />
                <AuthedRoute path="/admins/" component={AdminList} />
                <AuthedRoute
                  path="/messages/send"
                  component={SendMessageContainer}
                />
                <AuthedRoute path="/messages/new" component={MessageNew} />
                <AuthedRoute
                  path="/support/:supportRequestId"
                  component={SupportRequestContainer}
                />
                <AuthedRoute
                  path="/support"
                  component={SupportRequestsContainer}
                />
                <AuthedRoute
                  path="/messages/:messageableType/:messageableId/new"
                  component={AssociatedMessageNew}
                />
                <AuthedRoute
                  path="/messages/:messageId/edit"
                  component={MessageEdit}
                />
                <AuthedRoute
                  path="/messages/tab/:kind"
                  component={MessagesList}
                />
                <AuthedRoute
                  path="/messages"
                  render={() => <Redirect to="/messages/tab/active" />}
                />
                <AuthedRoute
                  path="/transactions/:transactionId"
                  component={TransactionContainer}
                />
                <AuthedRoute
                  path="/transactions"
                  component={TransactionsDashboardContainer}
                />
                <AuthedRoute
                  path="/qr-codes/:qrCodeId"
                  component={QrCodeShow}
                />
                <AuthedRoute path="/qr-codes" component={QrCodeList} />
                <AuthedRoute path="/quicksight" component={QuickSight} />
                <AuthedRoute
                  path="/bonuses/:userId/bonuses/new"
                  component={BonusNew}
                />
                <AuthedRoute path="/multibonus/new" component={Multibonus} />
                <AuthedRoute path="/multibonus" component={ScheduleBonusList} />
                <Route path="*" component={NoMatch} />
              </Switch>
            </div>
          </div>
        </div>
      </RelayEnvironmentProvider>
    </BrowserRouter>
  );
};

export default AppRouter;
